import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "counter__selection" }
const _hoisted_2 = ["innerHTML"]

import {useStore} from 'vuex';
    import {ref, inject, computed, onMounted, onBeforeUnmount} from 'vue';
    import {useGlobalProperties, useTranslation} from '@malt/vue3-use';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import getters from '@/profile-creation-wizard/store/creation/getters';
    import {PricingAverageResponse} from '@/profile-creation-wizard/components/for-creation/PricingStatService';
    import {eventBusProfileCreationWizard} from '@/profile-creation-wizard/common/events';
    import mutations from '@/profile-creation-wizard/store/creation/mutations';
    import {TipsPart} from '@/profile-creation-wizard/components/for-creation/TipsPart';
    import {escapeHtml} from '@malt/sanitize';
    import {VJoyCounter} from '@maltjoy/core-vue';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'PriceSelection',
  setup(__props) {

    const store = useStore();
    const {msg} = useTranslation();
    const {$pageContext} = useGlobalProperties();

    const pricingStatsService = inject(DI.pricingStatsService);
    const creationForm = ref(store.getters[getters.creationForm]);
    const selectedCategories = ref(store.getters[getters.selectedCategories]);

    const counter = ref<InstanceType<typeof VJoyCounter>>();
    const counterErrorMsg = ref<string | null>(null);
    const debounceTimeout = ref(0);
    const priceSelected = ref<number>(0);
    const estimateEarningStandard = ref<number>(0);
    const estimateEarningReduced = ref<number>(0);
    const feeRateStandard = ref<number>(10);
    const feeRateReduced = ref<number>(5);
    const mobileSM = ref<boolean>(false);
    const average = ref<PricingAverageResponse>(null);
    const averageDisplayLatch = ref<boolean>(false);
    const isFunnelStrategy = computed(() => store.state.isFunnelStrategy);
    const showAverage = computed(() => average.value !== null && average.value.minPrice > 0 && average.value.maxPrice > 0);
    const currency = computed(() => $pageContext.currentCurrency.symbol);
    const description = computed(() =>
        msg(
            'profile.creation.completion.wizard.details.price.description',
            escapeHtml(average.value!.minPrice),
            escapeHtml(currency.value),
            escapeHtml(average.value!.maxPrice),
            escapeHtml(currency.value),
        ),
    );
    const hasEstimateEarning = computed(() => $pageContext.mandatoryDetails.standardFreelancerFeeRate > 0 && estimateEarningStandard.value > 0);
    const priceConfiguration = computed(() => store.getters[getters.priceConfiguration]);

    onMounted(async () => {
        eventBusProfileCreationWizard.on('creation:experience-level-updated', updatePricingAverage);
        window.addEventListener('resize', onResize);
        await init();
    });

    async function init(): Promise<void> {
        mobileSM.value = isMobileSM();
        priceSelected.value = creationForm.value.price;
        await updatePricingAverage();
        if (priceSelected.value > 0) {
            computeEstimateEarnings();
        }
    }

    onBeforeUnmount(() => {
        eventBusProfileCreationWizard.off('creation:experience-level-updated', updatePricingAverage);
        window.removeEventListener('resize', onResize);
    });

    function onResize(): void {
        mobileSM.value = isMobileSM();
    }

    function isMobileSM(): boolean {
        return window.matchMedia('(max-width: 991px)').matches;
    }

    async function updatePricingAverage(): Promise<void> {
        const request = {
            category: selectedCategories.value[0],
            experienceLevel: creationForm.value.experienceLevel!,
            countryCode: $pageContext.currentCountryCode!,
        };

        const [response, error] = await pricingStatsService!.getAverage(request);

        if (response && !error) {
            average.value = response;
        }
    }

    async function changeValue(value: number): Promise<void> {
        priceSelected.value = value;

        emitFocus();
        store.commit(mutations.selectPrice, value);
        if (debounceTimeout.value) {
            clearTimeout(debounceTimeout.value);
        }

        averageDisplayLatch.value = false;
        debounceTimeout.value = setTimeout(async () => {
            computeEstimateEarnings();
            averageDisplayLatch.value = true;
        }, 500);
    }

    function emitFocus(): void {
        eventBusProfileCreationWizard.emit('creation:focus-changed', TipsPart.price);
    }

    function computeEstimateEarnings(): void {
        estimateEarningStandard.value = Math.ceil(creationForm.value.price * (1 - feeRateStandard.value / 100));
        estimateEarningReduced.value = Math.ceil(creationForm.value.price * (1 - feeRateReduced.value / 100));
    }

    function displayErrorIfNeeded() {
        counterErrorMsg.value = counter.value.$el.querySelector('input')?.validationMessage;
    }

return (_ctx: any,_cache: any) => {
  const _component_VJoyFormError = _resolveComponent("VJoyFormError")!
  const _component_VJoyIcon = _resolveComponent("VJoyIcon")!
  const _component_VJoyTooltip = _resolveComponent("VJoyTooltip")!
  const _component_VJoyHighlight = _resolveComponent("VJoyHighlight")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(VJoyCounter), {
      ref_key: "counter",
      ref: counter,
      modelValue: priceSelected.value,
      "onUpdate:modelValue": [
        _cache[0] || (_cache[0] = ($event: any) => ((priceSelected).value = $event)),
        changeValue
      ],
      name: "counter",
      "aria-label": _unref(msg)('profile.creation.completion.wizard.details.price.title'),
      min: Number(priceConfiguration.value.minPriceAllowed),
      max: Number(priceConfiguration.value.maxPriceAllowed),
      step: 50,
      required: true,
      "data-testid": "price-input",
      onFocusCapture: emitFocus,
      onBlur: displayErrorIfNeeded
    }, null, 8, ["modelValue", "aria-label", "min", "max"]),
    (counterErrorMsg.value)
      ? (_openBlock(), _createBlock(_component_VJoyFormError, {
          key: 0,
          class: "joy-mt-4 joy-mb-1"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(counterErrorMsg.value), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _withDirectives(_createVNode(_component_VJoyHighlight, {
      class: "counter__selection-earning",
      level: "neutral",
      "data-testid": "priceWithoutFee"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          _createTextVNode(_toDisplayString(_unref(msg)('profile.creation.completion.wizard.details.price.earning')) + " ", 1),
          _createElementVNode("span", null, [
            _createElementVNode("strong", null, _toDisplayString(estimateEarningStandard.value) + " - " + _toDisplayString(estimateEarningReduced.value) + _toDisplayString(currency.value), 1)
          ]),
          (!mobileSM.value)
            ? (_openBlock(), _createBlock(_component_VJoyTooltip, {
                key: 0,
                class: "counter__selection-earning__tooltip",
                position: 'bottom-end'
              }, {
                "tooltip-content": _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(msg)('profile.wizard.price.tooltip')), 1)
                ]),
                default: _withCtx(() => [
                  _createVNode(_component_VJoyIcon, {
                    name: "info-circle",
                    size: "xsmall"
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 512), [
      [_vShow, hasEstimateEarning.value]
    ]),
    (!isFunnelStrategy.value && showAverage.value)
      ? (_openBlock(), _createElementBlock("p", {
          key: 1,
          class: "counter__selection-description",
          innerHTML: description.value
        }, null, 8, _hoisted_2))
      : _createCommentVNode("", true)
  ]))
}
}

})